import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { Header } from './components/Header';
import { ProductHero } from './components/ProductHero';
import { ProductBenefits } from './components/ProductBenefits';
import { RecipesSample } from './components/RecipesSample';
import { Footer } from './components/Footer';
// Lazy load components below the fold
const VideoSection = lazy(() => import('./components/VideoSection').then(module => ({
  default: module.VideoSection
})));
const SpecialOffer = lazy(() => import('./components/SpecialOffer').then(module => ({
  default: module.SpecialOffer
})));
const ProductDescription = lazy(() => import('./components/ProductDescription').then(module => ({
  default: module.ProductDescription
})));
const HowToUse = lazy(() => import('./components/HowToUse').then(module => ({
  default: module.HowToUse
})));
const ResultsSection = lazy(() => import('./components/ResultsSection').then(module => ({
  default: module.ResultsSection
})));
const BundleShowcase = lazy(() => import('./components/BundleShowcase').then(module => ({
  default: module.BundleShowcase
})));
const HowItWorks = lazy(() => import('./components/HowItWorks').then(module => ({
  default: module.HowItWorks
})));
const TreatmentTypes = lazy(() => import('./components/TreatmentTypes').then(module => ({
  default: module.TreatmentTypes
})));
const FAQ = lazy(() => import('./components/FAQ').then(module => ({
  default: module.FAQ
})));
const Reviews = lazy(() => import('./components/Reviews').then(module => ({
  default: module.Reviews
})));
const RelatedProducts = lazy(() => import('./components/RelatedProducts').then(module => ({
  default: module.RelatedProducts
})));
// Simple loading fallback component
const LoadingFallback = () => <div className="py-8" data-id="element-0" />;
export function App() {
  // Set document title and meta tags for SEO
  useEffect(() => {
    // Document title
    document.title = '50 Recetas de 15 Minutos - Pack Premium de Cocina Rápida | $9.95 USD';
    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Descubre 50 recetas deliciosas listas en solo 15 minutos. Pack premium con 7 libros de cocina incluidos. Oferta especial: $250 de valor por solo $9.95 USD. Ideal para mamás ocupadas y familias modernas.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Descubre 50 recetas deliciosas listas en solo 15 minutos. Pack premium con 7 libros de cocina incluidos. Oferta especial: $250 de valor por solo $9.95 USD. Ideal para mamás ocupadas y familias modernas.';
      document.head.appendChild(meta);
    }
    // Keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'recetas rápidas, cocina 15 minutos, recetas fáciles, libro de cocina, recetas saludables, comida rápida casera, batch cooking, meal prep, recetas para mamás ocupadas');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'recetas rápidas, cocina 15 minutos, recetas fáciles, libro de cocina, recetas saludables, comida rápida casera, batch cooking, meal prep, recetas para mamás ocupadas';
      document.head.appendChild(meta);
    }
    // Open Graph tags for social media
    const ogTags = [{
      property: 'og:title',
      content: '50 Recetas de 15 Minutos - Pack Premium de Cocina Rápida'
    }, {
      property: 'og:description',
      content: 'Pack premium con 7 libros de cocina. $250 de valor por solo $9.95 USD. Recetas deliciosas listas en 15 minutos.'
    }, {
      property: 'og:type',
      content: 'product'
    }, {
      property: 'og:locale',
      content: 'es_ES'
    }, {
      property: 'og:site_name',
      content: '50 Recetas de 15 Minutos'
    }];
    ogTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (metaTag) {
        metaTag.setAttribute('content', tag.content);
      } else {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', tag.property);
        metaTag.setAttribute('content', tag.content);
        document.head.appendChild(metaTag);
      }
    });
    // Twitter Card tags
    const twitterTags = [{
      name: 'twitter:card',
      content: 'summary_large_image'
    }, {
      name: 'twitter:title',
      content: '50 Recetas de 15 Minutos - Pack Premium'
    }, {
      name: 'twitter:description',
      content: 'Pack premium con 7 libros de cocina. $250 de valor por solo $9.95 USD.'
    }];
    twitterTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[name="${tag.name}"]`);
      if (metaTag) {
        metaTag.setAttribute('content', tag.content);
      } else {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', tag.name);
        metaTag.setAttribute('content', tag.content);
        document.head.appendChild(metaTag);
      }
    });
    // Viewport meta tag for responsive design
    let viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5.0');
    }
    // Language
    document.documentElement.lang = 'es';
    // Add structured data (JSON-LD) for SEO
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: '50 Recetas de 15 Minutos - Pack Premium',
      description: 'Pack premium con 7 libros de cocina digital. Recetas deliciosas listas en solo 15 minutos, perfectas para mamás ocupadas y familias modernas.',
      offers: {
        '@type': 'Offer',
        price: '9.95',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        priceValidUntil: '2025-12-31',
        url: window.location.href
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '1247'
      },
      brand: {
        '@type': 'Brand',
        name: 'Kiosco Digital'
      }
    };
    const recipeStructuredData = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: [{
        '@type': 'Recipe',
        name: '50 Recetas de 15 Minutos',
        description: 'Colección de 50 recetas rápidas y deliciosas que se preparan en solo 15 minutos',
        prepTime: 'PT15M',
        cookTime: 'PT15M',
        totalTime: 'PT15M'
      }]
    };
    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());
    // Add product structured data
    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(structuredData);
    document.head.appendChild(script1);
    // Add recipe structured data
    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(recipeStructuredData);
    document.head.appendChild(script2);
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-1">
      <Header data-id="element-2" />
      <main data-id="element-3">
        <ProductHero data-id="element-4" />
        <ProductBenefits data-id="element-5" />
        <RecipesSample data-id="element-6" />
        {/* Split into multiple Suspense boundaries to reduce load */}
        <Suspense fallback={<LoadingFallback data-id="element-8" />} data-id="element-7">
          <VideoSection data-id="element-9" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-11" />} data-id="element-10">
          <SpecialOffer data-id="element-12" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-14" />} data-id="element-13">
          <ProductDescription data-id="element-15" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-17" />} data-id="element-16">
          <HowToUse data-id="element-18" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-20" />} data-id="element-19">
          <ResultsSection data-id="element-21" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-23" />} data-id="element-22">
          <BundleShowcase data-id="element-24" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-26" />} data-id="element-25">
          <HowItWorks data-id="element-27" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-29" />} data-id="element-28">
          <TreatmentTypes data-id="element-30" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-32" />} data-id="element-31">
          <FAQ data-id="element-33" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-35" />} data-id="element-34">
          <Reviews data-id="element-36" />
        </Suspense>
        <Suspense fallback={<LoadingFallback data-id="element-38" />} data-id="element-37">
          <RelatedProducts data-id="element-39" />
        </Suspense>
      </main>
      <Footer data-id="element-40" />
    </div>;
}